import { ReactNode } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { secondaryBlueTransition } from 'shared/components/mixins';

const fadeIn = keyframes`
  0% {
      opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const ModalScreen = styled.div`
  align-items: center;
  display: flex;
  height: 100vh;
  justify-content: center;
  left: 0;
  position: fixed;
  top: 0;
  width: 100vw;
  z-index: 1337;
`;

const ModalBackdrop = styled.div`
  animation: ${fadeIn} 0.5s;
  background: rgba(0, 0, 0, 0.2);
  height: 100%;
  width: 100%;
  left: 0;
  position: absolute;
  top: 0;
`;

const ModalContainer = styled.div`
  box-shadow: ${({ theme }) => theme.SHADOWS.PRIMARY};
  border-radius: ${({ theme }) => theme.BORDER_RADIUS};
  background-color: #ffffff;
  padding: 24px;
  position: relative;
`;

const CloseButton = styled.button`
  border: none;
  border-radius: 50%;
  cursor: pointer;
  height: 18px;
  padding: 16px;
  position: absolute;
  right: 10px;
  top: 10px;
  width: 18px;
  z-index: 1;

  ${secondaryBlueTransition};

  &:before,
  &:after {
    border-radius: ${({ theme }) => theme.BORDER_RADIUS};
    background-color: ${({ theme }) => theme.COLORS.PRIMARY};
    content: '';
    display: block;
    height: 2px;
    position: absolute;
    left: 50%;
    top: 50%;
    width: 60%;
  }

  &:before {
    transform: translate(-50%) rotate(45deg);
  }

  &:after {
    transform: translate(-50%) rotate(-45deg);
  }
`;

type ModalProps = {
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  className?: string;
};

function Modal({ children, isOpen, className, onClose }: ModalProps): JSX.Element | null {
  return isOpen
    ? ReactDOM.createPortal(
        <ModalScreen className={className}>
          <ModalBackdrop onClick={onClose} />
          <ModalContainer role="dialog">{children}</ModalContainer>
        </ModalScreen>,
        document.body,
      )
    : null;
}

Modal.CloseButton = CloseButton;

export default Modal;
